import { getApiBaseUrl, getAuthHeaders } from '@/config/api';

export interface ChatMonitorSource {
  id: string;
  portalName: string;
  portalUrl?: string | null;
  sourceType: string;
  companyName: string;
  companyId?: string | null;
  enabled: boolean;
  mentionTerms: string;
  notifyEmail: boolean;
  notifyWhatsapp: boolean;
  lastCheckedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: { events: number };
}

export interface ChatMonitoredTender {
  id: string;
  sourceId: string;
  source?: ChatMonitorSource;
  tenderRef: string;
  externalLotId?: string | null;
  organ?: string | null;
  object?: string | null;
  sessionUrl?: string | null;
  roomStatus?: string | null;
  status: string;
  enabled: boolean;
  notes?: string | null;
  lastCheckedAt?: string | null;
  roomCheckedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMentionEvent {
  id: string;
  sourceId: string;
  source?: ChatMonitorSource;
  portalName: string;
  tenderRef?: string | null;
  senderName?: string | null;
  messageText: string;
  matchedTerms: string;
  directMention: boolean;
  occurredAt: string;
  status: string;
  emailStatus?: string | null;
  whatsappStatus?: string | null;
  pushStatus?: string | null;
  emailError?: string | null;
  whatsappError?: string | null;
  pushError?: string | null;
  verifiedAt?: string | null;
  verifiedBy?: string | null;
  archiveNote?: string | null;
  archivedAt?: string | null;
  createdAt: string;
}

export interface ChatIngestPayload {
  sourceId?: string;
  portalName?: string;
  tenderRef?: string;
  senderName?: string;
  messageText: string;
  occurredAt?: string;
  rawPayload?: unknown;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...(init?.headers || {}),
    },
  });

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.error || `Erro HTTP ${response.status}`);
  }
  return data as T;
}

export const chatMonitoringService = {
  listSources() {
    return request<ChatMonitorSource[]>('/chat-monitoring/sources');
  },

  createSource(payload: Partial<ChatMonitorSource>) {
    return request<ChatMonitorSource>('/chat-monitoring/sources', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  updateSource(id: string, payload: Partial<ChatMonitorSource>) {
    return request<ChatMonitorSource>(`/chat-monitoring/sources/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  deleteSource(id: string) {
    return request<{ success: boolean }>(`/chat-monitoring/sources/${id}`, {
      method: 'DELETE',
    });
  },

  listMonitoredTenders(sourceId?: string) {
    const query = sourceId ? `?sourceId=${encodeURIComponent(sourceId)}` : '';
    return request<ChatMonitoredTender[]>(`/chat-monitoring/monitored-tenders${query}`);
  },

  saveMonitoredTender(payload: Partial<ChatMonitoredTender>) {
    if (payload.id) {
      return request<ChatMonitoredTender>(`/chat-monitoring/monitored-tenders/${payload.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    }
    return request<ChatMonitoredTender>('/chat-monitoring/monitored-tenders', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  deleteMonitoredTender(id: string) {
    return request<{ success: boolean }>(`/chat-monitoring/monitored-tenders/${id}`, {
      method: 'DELETE',
    });
  },

  listEvents(limit = 100, archived = false) {
    return request<ChatMentionEvent[]>(`/chat-monitoring/events?limit=${limit}&archived=${archived ? 'true' : 'false'}`);
  },

  getEventCounts() {
    return request<{ pending: number; archived: number; lastEvent?: { id: string; occurredAt: string } | null }>('/chat-monitoring/events/counts');
  },

  ingest(payload: ChatIngestPayload) {
    return request<{ matched: boolean; events: ChatMentionEvent[]; sourcesChecked: number; duplicatesIgnored: number }>('/chat-monitoring/ingest', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  retryNotification(eventId: string) {
    return request<ChatMentionEvent>(`/chat-monitoring/events/${eventId}/notify`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  verifyEvent(eventId: string, payload?: { verifiedBy?: string; archiveNote?: string }) {
    return request<ChatMentionEvent>(`/chat-monitoring/events/${eventId}/verify`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  },

  verifyTender(tenderRef: string, payload?: { verifiedBy?: string; archiveNote?: string }) {
    return request<{ success: boolean; count: number }>('/chat-monitoring/tenders/verify', {
      method: 'POST',
      body: JSON.stringify({ tenderRef, ...(payload || {}) }),
    });
  },
};
